import { useEffect } from 'react';
import { User } from 'lucide-react';
import { Sheet, SheetContent } from '@/core/components/ui/sheet';
import { BladeHeader, BladeStatusBanner } from '@/core/components/ui/detail-blade';
import { KioskSessionGridRow } from '../types/kiosk-session-types';
import { useKioskSessionsStore } from '../stores/kiosk-sessions-store';
import { SessionInfoSection } from './SessionInfoSection';
import { PaymentInfoSection } from './PaymentInfoSection';
import { EnergyInfoSection } from './EnergyInfoSection';
import { LocationInfoSection } from './LocationInfoSection';
import { CostBreakdownSection } from './CostBreakdownSection';

interface KioskSessionDetailBladeProps {
  session: KioskSessionGridRow | null;
  isOpen: boolean;
  onClose: () => void;
  fallbackCurrency?: string;
}

export function KioskSessionDetailBlade({
  session,
  isOpen,
  onClose,
  fallbackCurrency
}: KioskSessionDetailBladeProps) {
  const {
    selectedSessionDetail,
    selectedSessionId,
    setSelectedSessionFromGrid,
    clearSelection,
  } = useKioskSessionsStore();

  // Set selected session from grid data when session prop changes
  useEffect(() => {
    if (session && isOpen) {
      setSelectedSessionFromGrid(session);
    }
  }, [session, isOpen, setSelectedSessionFromGrid]);

  const handleClose = () => {
    clearSelection();
    onClose();
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent className="w-full sm:max-w-xl lg:max-w-2xl overflow-y-auto" data-testid="detail-blade">
        <BladeHeader
          title="Session Details"
          subtitle={session ? `Session ID: ${session.session_id}` : undefined}
          icon={User}
          onClose={handleClose}
          showCloseButton={false}
          className="pt-2 pr-12"
        />

        {/* Status banner for failed payments */}
        {selectedSessionDetail?.authorization_result === 'DECLINED' && (
          <BladeStatusBanner
            status="error"
            title="Blocked Due To Debt"
            message="This session was declined due to outstanding payment issues."
            dismissible={true}
          />
        )}

        {/* Session Detail Content */}
        {selectedSessionDetail && session && (
          <div className="space-y-6">
            <SessionInfoSection
              session={session}
              detail={selectedSessionDetail}
            />

            <PaymentInfoSection
              session={session}
              detail={selectedSessionDetail}
              fallbackCurrency={fallbackCurrency}
            />

            <EnergyInfoSection
              session={session}
              detail={selectedSessionDetail}
            />

            <CostBreakdownSection
              session={session}
              detail={selectedSessionDetail}
              fallbackCurrency={fallbackCurrency}
            />

            <LocationInfoSection
              session={session}
              detail={selectedSessionDetail}
            />
          </div>
        )}

        {/* No Session Selected */}
        {!session && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <p className="text-gray-500">No session selected</p>
              <p className="text-sm text-gray-400 mt-1">
                Select a session from the grid to view details
              </p>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}